import { API_CONFIG } from '../../config/api.config.js'
import { authService } from './auth.service.js'
import { AUTH_CONSTANTS, createAuthTimeoutError } from '../constants/auth.constants.js'

/**
 * Serviço de API para comunicação com backend v2
 * Centraliza configuração e interceptadores de requisições
 * Implementa cache inteligente de token para otimização
 */
export class ApiService {
  /**
   * Inicializa serviço de API com cache de token
   */
  constructor() {
    this.baseURL = API_CONFIG.BASE_URL
    this.timeout = API_CONFIG.TIMEOUT
    
    // Cache de token para otimização
    this.tokenCache = null
    this.tokenExpiry = null
    this.isRefreshing = false
    this.refreshPromise = null
  }

  /**
   * Obtém token de autenticação Firebase com cache inteligente
   * Reduz 90%+ das chamadas desnecessárias ao Firebase Auth
   * @param {boolean} forceRefresh - Força renovação do token
   * @returns {Promise<string>} Token JWT
   */
  async getAuthToken(forceRefresh = false) {
    // Retorna token do cache se válido
    if (!forceRefresh && this.tokenCache && this.tokenExpiry > Date.now()) {
      return this.tokenCache
    }

    // Evita múltiplas chamadas simultâneas durante refresh
    if (this.isRefreshing && this.refreshPromise) {
      return await this.refreshPromise
    }

    // Inicia processo de refresh
    this.isRefreshing = true
    this.refreshPromise = this._refreshToken()
    
    try {
      const token = await this.refreshPromise
      return token
    } finally {
      this.isRefreshing = false
      this.refreshPromise = null
    }
  }

  /**
   * Renova token do Firebase e atualiza cache
   * @private
   * @returns {Promise<string>} Novo token
   */
  async _refreshToken() {
    const { getAuth } = await import('firebase/auth')
    const auth = getAuth()
    
    if (!auth.currentUser) {
      this._clearTokenCache()
      throw new Error('Usuário não autenticado')
    }

    try {
      const token = await auth.currentUser.getIdToken(true) // Force refresh
      
      // Atualiza cache com TTL de 50 minutos (token expira em 1h)
      this.tokenCache = token
      this.tokenExpiry = Date.now() + (50 * 60 * 1000)
      
      return token
    } catch (error) {
      this._clearTokenCache()
      throw new Error('Erro ao renovar token: ' + error.message)
    }
  }

  /**
   * Limpa cache de token
   * @private
   */
  _clearTokenCache() {
    this.tokenCache = null
    this.tokenExpiry = null
  }

  /**
   * Aguarda resolução do estado de autenticação com timeout de segurança.
   * Se o auth já está pronto (_authReady === true), resolve imediatamente sem delay.
   * Caso contrário, utiliza Promise.race() entre waitForAuth() e um timeout de 10s.
   * Garante que nenhuma requisição fique bloqueada indefinidamente se o Firebase falhar.
   *
   * @private
   * @returns {Promise<void>} Resolve quando auth está pronto
   * @throws {Error} Erro com code='AUTH_TIMEOUT' se não resolver em AUTH_CONSTANTS.AUTH_TIMEOUT ms
   */
  async _waitForAuthWithTimeout () {
    // Se auth já está resolvido, retorna imediatamente sem delay
    if (authService._authReady) {
      return Promise.resolve()
    }

    let timeoutId = null

    // Promise de timeout que rejeita após AUTH_TIMEOUT ms
    const timeoutPromise = new Promise((_, reject) => {
      timeoutId = setTimeout(() => {
        reject(createAuthTimeoutError())
      }, AUTH_CONSTANTS.AUTH_TIMEOUT)
    })

    try {
      // Corrida entre auth resolver e timeout expirar
      await Promise.race([
        authService.waitForAuth(),
        timeoutPromise
      ])
    } finally {
      // Limpa timeout independentemente do resultado (resolve ou reject)
      if (timeoutId !== null) {
        clearTimeout(timeoutId)
      }
    }
  }

  /**
   * Constrói headers padrão para requisições com token cacheado
   * @returns {Promise<Object>} Headers com autenticação
   */
  async buildHeaders() {
    const headers = {
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    }

    try {
      const token = await this.getAuthToken()
      headers.Authorization = `Bearer ${token}`
    } catch (error) {
      console.warn('⚠️ Token não disponível:', error.message)
    }

    return headers
  }

  /**
   * Executa requisição HTTP com tratamento de erros e retry automático.
   * Em caso de resposta 401, invalida cache de token, renova via getAuthToken(true)
   * e executa exatamente 1 retry com o novo token — sem re-aguardar waitForAuth().
   * Se o retry também falhar, propaga erro e limpa cache de token local.
   *
   * @param {string} endpoint - Endpoint da API
   * @param {Object} options - Opções da requisição
   * @returns {Promise<Object>} Resposta da API
   * @throws {Error} Erro tratado se requisição falhar após retry
   */
  async request(endpoint, options = {}) {
    // Barreira de autenticação: aguarda auth estar resolvido antes de prosseguir
    // Se auth já está pronto, resolve imediatamente sem delay (0ms)
    // Se não está pronto, aguarda até resolver ou timeout de 10s
    await this._waitForAuthWithTimeout()

    // Remover barra inicial do endpoint se existir para evitar barra dupla
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint
    const url = `${this.baseURL}/${cleanEndpoint}`

    try {
      return await this._makeRequest(url, options)
    } catch (error) {
      // Retry com token renovado se erro 401 (exatamente 1 tentativa)
      // Flag _isRetry impede loop infinito — garante no máximo 1 retry
      // NÃO re-aguarda auth (waitForAuth) — auth já está resolvido neste ponto
      if (error.status === 401 && !options._isRetry) {
        try {
          // Invalida cache de token expirado antes de renovar
          this._clearTokenCache()
          // Renova token via Firebase getIdToken(true) forçando refresh
          await this.getAuthToken(true)
          // Executa exatamente 1 retry com novo token
          const retryOptions = { ...options, _isRetry: true }
          return await this._makeRequest(url, retryOptions)
        } catch (retryError) {
          // Se retry falhar, limpa cache de token e propaga erro ao chamador
          this._clearTokenCache()
          throw this.handleError(retryError)
        }
      }

      throw this.handleError(error)
    }
  }

  /**
   * Executa requisição HTTP
   * @private
   * @param {string} url - URL completa
   * @param {Object} options - Opções da requisição
   * @returns {Promise<Object>} Resposta da API
   */
  async _makeRequest(url, options) {
    const headers = await this.buildHeaders()
    
    const config = {
      ...options,
      headers: { ...headers, ...options.headers }
    }

    const response = await fetch(url, config)
    
    if (!response.ok) {
      const error = new Error(`HTTP ${response.status}: ${response.statusText}`)
      error.status = response.status
      throw error
    }

    const contentType = response.headers.get('content-type')
    
    if (contentType && contentType.includes('application/json')) {
      return await response.json()
    }
    
    return { data: await response.text() }
  }

  /**
   * Requisição GET
   * @param {string} endpoint - Endpoint da API
   * @param {Object} params - Parâmetros de query
   * @returns {Promise<Object>} Resposta da API
   */
  async get(endpoint, params = {}) {
    let finalEndpoint = endpoint
    
    if (Object.keys(params).length > 0) {
      const searchParams = new URLSearchParams()
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.set(key, value)
        }
      })
      finalEndpoint += `?${searchParams.toString()}`
    }
    
    return this.request(finalEndpoint, { method: 'GET' })
  }

  /**
   * Requisição POST
   * @param {string} endpoint - Endpoint da API
   * @param {Object} data - Dados para envio
   * @returns {Promise<Object>} Resposta da API
   */
  async post(endpoint, data = {}) {
    return this.request(endpoint, {
      method: 'POST',
      body: JSON.stringify(data)
    })
  }

  /**
   * Requisição PUT
   * @param {string} endpoint - Endpoint da API
   * @param {Object} data - Dados para envio
   * @returns {Promise<Object>} Resposta da API
   */
  async put(endpoint, data = {}) {
    return this.request(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data)
    })
  }

  /**
   * Requisição DELETE
   * @param {string} endpoint - Endpoint da API
   * @returns {Promise<Object>} Resposta da API
   */
  async delete(endpoint) {
    return this.request(endpoint, { method: 'DELETE' })
  }

  /**
   * Invalida cache de token (força renovação na próxima chamada)
   * Útil após logout ou erro de autenticação
   */
  invalidateTokenCache() {
    this._clearTokenCache()
  }

  /**
   * Verifica se token está em cache e válido
   * @returns {boolean} True se token cacheado é válido
   */
  hasValidToken() {
    return !!(this.tokenCache && this.tokenExpiry > Date.now())
  }

  /**
   * Obtém informações do cache de token (para debug)
   * @returns {Object} Status do cache
   */
  getTokenCacheInfo() {
    return {
      hasToken: !!this.tokenCache,
      isValid: this.hasValidToken(),
      expiresIn: this.tokenExpiry ? Math.max(0, this.tokenExpiry - Date.now()) : 0,
      isRefreshing: this.isRefreshing
    }
  }

  /**
   * Trata erros de requisição
   * @param {Error} error - Erro original
   * @returns {Error} Erro tratado
   */
  handleError(error) {
    const errorMap = {
      400: 'Dados inválidos fornecidos',
      401: 'Não autorizado - faça login novamente',
      402: 'Acesso premium necessário',
      403: 'Acesso negado',
      404: 'Recurso não encontrado',
      409: 'Conflito - recurso já existe',
      422: 'Dados não processáveis',
      500: 'Erro interno do servidor'
    }

    // Limpa cache em caso de erro de autenticação
    if (error.status === 401) {
      this._clearTokenCache()
    }

    if (error.message.includes('HTTP') || error.status) {
      const status = error.status || parseInt(error.message.match(/HTTP (\d+)/)?.[1])
      const friendlyMessage = errorMap[status] || 'Erro desconhecido'
      
      const customError = new Error(friendlyMessage)
      customError.status = status
      customError.code = status === 402 ? 'PREMIUM_REQUIRED' : null
      customError.originalError = error
      
      return customError
    }

    return error
  }
}

// Instância singleton
export const apiService = new ApiService()

// Limpa cache quando usuário faz logout
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    apiService.invalidateTokenCache()
  })
}